import { useMemo } from "react"
import { isEmpty, omitBy } from "lodash"
import { addOrReplaceAction, createAction } from "helpers/simulator"
import useResource from "./resources"
import { OK } from "helpers/data-statuses"

export const POSITIONS_SUBQUERY = `
equity
date
equities {
    id {
        ticker
        mic
        figi
        bloombergTicker
        modelProviderId
        internalId
    }
    lotId
    economicExposure
    attributes {
        category
        value
    }
}
currencies {
    id
    economicExposure
    lotId
    attributes {
        value
        category
    }
}
swaps {
    id
    economicExposure
    lotId
    attributes {
        value
        category
    }
}
fixedIncome {
    id {
        isin
    }
    economicExposure
    lotId
    attributes {
        value
        category
    }
}
commodities {
    id
    economicExposure
    lotId
    attributes {
        value
        category
    }
}
indexes {
    id
    economicExposure
    lotId
    attributes {
        value
        category
    }
}
otherAssets {
    id
    economicExposure
    lotId
    attributes {
        value
        category
    }
}
`

const PORTFOLIO_HOLDINGS_QUERY = `
query ($modelId: String!, $on: Date!, $portfolioId: String!) {
  portfolio(id: $portfolioId) {
    dates(from: $on, to: $on, modelId: $modelId) {
      ${POSITIONS_SUBQUERY}
    }
  }
}`

const EXPERIMENT_HOLDINGS_QUERY = `
query ($on: Date!, $portfolioId: String!, $experimentId: String!) {
  portfolio(id: $portfolioId) {
    experiment(id: $experimentId) {
      dates(from: $on, to: $on) {
        ${POSITIONS_SUBQUERY}
      }
    }
  }
}`

const REBALANCE_QUERY = `
query ($modelId: String!, $on: Date!, $actions: [ActionInput!], $positionSet: PositionSetInput!, $actionsBase: [PositionSetInput!], $batchId: Id) {
  model(id: $modelId) {
    simulateActions(
      positionSet: $positionSet
      actionsBase: $actionsBase
      on: $on
      actions: $actions
      batchId: $batchId
    ) {
      positions {
        ${POSITIONS_SUBQUERY}
      }
    }
  }
}`

export function useSimulateActions(trades) {
  return useMemo(() => {
    let simulateActions = []

    if (!isEmpty(trades)) {
      for (const trade of trades) {
        // Can't process filters here because they need to be computed at the time of the trade creation
        // Grouping also doesn't apply because we're always using the AddTrade workflow
        const { security: position, field, value, filter } = trade
        simulateActions = addOrReplaceAction(simulateActions, createAction({ position, field, value, filters: filter }))
      }
    }

    return simulateActions
  }, [trades])
}

export function useRebalancer(trades, selectedInstrument, modelId, date, actionsBase, batchId) {
  const simulateActions = useSimulateActions(trades)
  const [rebalancedPositions, rebalancedPositionsStatus] = useResource({
    resource: "rebalancedResults",
    url: () => {
      if (isEmpty(simulateActions) || !modelId || !date || !selectedInstrument) {
        return null
      }

      return {
        id: `rebalancedResults-${date}-${modelId}-${JSON.stringify(selectedInstrument)}-${JSON.stringify(simulateActions)}-${batchId}`,
        query: REBALANCE_QUERY,
        variables: {
          modelId,
          on: date,
          actions: simulateActions,
          positionSet: selectedInstrument,
          actionsBase,
          batchId,
        },
      }
    },
  }, ["model", "simulateActions", "positions"])

  const rebalancedPositionSet = useMemo(() => {
    if (rebalancedPositionsStatus !== OK || isEmpty(rebalancedPositions)) {
      return null
    }

    return { dates: [rebalancedPositions] }
  }, [rebalancedPositions, rebalancedPositionsStatus])

  return [rebalancedPositionSet, rebalancedPositionsStatus]
}

export function computeSimulationExperiment(rebalancedPositionsDate, actionsBase, simulateActions, equityOverride, selectedDate) {
  if (isEmpty(rebalancedPositionsDate)) {
    return null
  }

  const config = omitBy({
    simulateActions,
    actionsBase,
  }, isEmpty)

  return omitBy({
    type: "SIMULATION",
    positionSet: {
      ...(equityOverride !== undefined ? { equity: equityOverride } : {}),
      ...(selectedDate !== undefined ? { date: selectedDate } : {}),
      ...rebalancedPositionsDate,
    },
    ...(!isEmpty(config) && { config }),
  }, isEmpty)
}

export function usePortfolioHoldings(instrument, modelId, date) {
  const portfolioId = instrument?.type === "PORTFOLIO" ? instrument.id : null
  const experimentId = instrument?.type === "PORTFOLIO" && instrument.experimentId
    ? instrument.experimentId
    : undefined

  const holdingsPath = useMemo(
    () => (experimentId ? ["portfolio", "experiment", "dates", 0] : ["portfolio", "dates", 0]),
    [experimentId],
  )

  // Must use a distinct Redux resource when experimentId is set; otherwise two hooks (e.g. optimizer +
  // save-experiment payload) fight over one `portfolioHoldings` slot and alternate loads forever.
  const portfolioHoldingsResource = experimentId ? "portfolioExperimentHoldings" : "portfolioHoldings"

  const portfolioHoldingsDefinition = useMemo(() => ({
    resource: portfolioHoldingsResource,
    url: () => {
      if (!date || !portfolioId) {
        return null
      }
      if (experimentId) {
        return {
          id: `portfolioExperimentHoldings-${date}-${JSON.stringify(portfolioId)}-exp-${experimentId}`,
          query: EXPERIMENT_HOLDINGS_QUERY,
          variables: {
            on: date,
            portfolioId,
            experimentId,
          },
        }
      }
      if (!modelId) {
        return null
      }
      return {
        id: `portfolioHoldings-${date}-${modelId}-${JSON.stringify(portfolioId)}`,
        query: PORTFOLIO_HOLDINGS_QUERY,
        variables: {
          modelId,
          on: date,
          portfolioId,
        },
      }
    },
  }), [modelId, date, portfolioId, experimentId, portfolioHoldingsResource])

  const [portfolioHoldings, portfolioHoldingsStatus] = useResource(portfolioHoldingsDefinition, holdingsPath)

  const portfolioHoldingsDate = useMemo(() => {
    if (portfolioHoldingsStatus !== OK || isEmpty(portfolioHoldings)) {
      return null
    }

    return portfolioHoldings
  }, [portfolioHoldings, portfolioHoldingsStatus])

  return [portfolioHoldingsDate, portfolioHoldingsStatus]
}
